<homey-page-title title="My Housing Connect account"/>

<p-message class="mt-4 mb-6" severity="warn" icon="pi pi-exclamation-triangle">
  Please don't make any changes directly in your Housing Connect account, as this can break Homey. If you need to make any changes, make them in Homey instead.
</p-message>

<homey-page-subtitle-layout>
  What is Housing Connect?
</homey-page-subtitle-layout>
<p class="text-muted-color">
  Housing Connect is NYC's official website for affordable housing lotteries. We've set up an account for you using your Homey details.
</p>
<p class="text-muted-color mb-6">
  You'll use it when one of your applications gets selected.
</p>

<homey-page-subtitle-layout class="mt-8">
  Accessing your account
</homey-page-subtitle-layout>

@let userValue = user();
@if (userValue) {
@if (userValue.status !== 'active') {
  <p class="text-muted-color">
    Since you’ve not yet completed your Homey profile, we haven’t yet created your Housing Connect account. Please complete your profile so we can create your Housing Connect account for you.
  </p>
} @else {
  @if (userValue.nycSyncStatus === 'not-synced') {
    <p class="text-muted-color">Since you've only just created your Homey account, we haven't yet created your Housing Connect account. Once you receive message that we've submitted your first applications, you'll be able to access your login credentials here.</p>
  } @else {
    <p class="text-muted-color">
      Go to
      <a class="text-primary font-medium underline" [href]="housingConnectUrl" target="_blank" rel="noopener">
        {{ housingConnectUrl }}
      </a>
      and use the following login details:
    </p>
    @if (hasError()) {
      <p-message severity="error" icon="pi pi-times-circle" class="mt-4">
        We couldn't load your Housing Connect login details right now. Please try again in a bit.
      </p-message>
    }

  <div class="rounded-2xl border border-surface-200 bg-surface-0 p-5 shadow-sm mt-4 space-y-5">
    <div class="flex flex-wrap items-center gap-3">
      <div class="min-w-0 flex-1">
        <p class="text-sm text-surface-600 mb-1">Username</p>
        @if (loading()) {
          <p-skeleton width="10rem" height="1.5rem"/>
        } @else if (hasAuth()) {
          <p class="font-mono text-base text-surface-900 font-normal break-all">{{ auth()?.username }}</p>
        }
      </div>
      <p-button
        severity="secondary"
        size="small"
        [disabled]="!hasAuth()"
        icon="pi pi-copy"
        styleClass="p-button-rounded"
        ariaLabel="Copy username"
        (onClick)="copy('username')"/>
    </div>

    <div class="h-px w-full bg-surface-200"></div>

    <div class="flex flex-wrap items-center gap-3">
      <div class="min-w-0 flex-1">
        <p class="text-sm text-surface-600 mb-1">Password</p>
        @if (loading()) {
          <p-skeleton width="8rem" height="1.5rem"/>
        } @else if (hasAuth()) {
          <p class="font-mono text-base text-surface-900 font-normal break-all">
            {{ passwordVisible() ? auth()?.password : getMaskedPassword() }}
          </p>
        }
      </div>
      <div class="flex items-center gap-3">
        <p-button
          severity="secondary"
          size="small"
          [disabled]="!hasAuth()"
          [icon]="passwordVisible() ? 'pi pi-eye-slash' : 'pi pi-eye'"
          styleClass="p-button-rounded"
          [ariaLabel]="passwordVisible() ? 'Hide password' : 'Reveal password'"
          (onClick)="togglePasswordVisibility()"/>
        <p-button
          severity="secondary"
          size="small"
          [disabled]="!hasAuth()"
          icon="pi pi-copy"
          styleClass="p-button-rounded"
          ariaLabel="Copy password"
          (onClick)="copy('password')"/>
      </div>
    </div>
  </div>


    <p class="text-muted-color mt-4">
      Once logged in, you'll see your applications, and you'll be able to upload documents proving your eligibility.
    </p>
    <homey-page-subtitle-layout class="mt-8">
      Privacy & Security
    </homey-page-subtitle-layout>
    <p class="text-muted-color">
      We understand you may have concerns about uploading sensitive documents, such as your SSN, to Housing Connect while Homey still has access to that account.
    </p>
    <p class="text-muted-color mt-2">
      If at any point you wish to have sole access to your Housing Connect account, please email us at <a class="underline text-blue-500" href="mailto:contact@myhomey.io">contact&#64;myhomey.io</a> and we'll transfer full access to you by letting you update the password and email of the account.
    </p>
  }
}
}
